import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import type { CatalogoItemDto } from '../../services/vehiculoService';

export interface CatalogoSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (item: CatalogoItemDto) => void;
  title: string;
  loadItems: (searchTerm: string) => Promise<CatalogoItemDto[]>;
  searchPlaceholder?: string;
}

const CatalogoSearchDialog: React.FC<CatalogoSearchDialogProps> = ({
  open,
  onClose,
  onSelect,
  title,
  loadItems,
  searchPlaceholder = 'Buscar...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<CatalogoItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchItems = useCallback(
    async (term: string) => {
      try {
        setLoading(true);
        setError(null);
        const data = await loadItems(term);
        setItems(data);
      } catch (err: unknown) {
        console.error('Error al cargar catálogo:', err);
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Error al cargar el catálogo';
        setError(msg);
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [loadItems],
  );

  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setItems([]);
      setError(null);
      return;
    }
    const timeoutId = setTimeout(() => {
      fetchItems(searchTerm);
    }, searchTerm ? 300 : 0);
    return () => clearTimeout(timeoutId);
  }, [open, searchTerm, fetchItems]);

  const handleSelect = (item: CatalogoItemDto) => {
    onSelect(item);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButton size="small" onClick={onClose} aria-label="Cerrar">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          autoFocus
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
        />
        {loading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : error ? (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No se encontraron resultados.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.codigo}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleSelect(item)}
                  >
                    <TableCell>{item.codigo}</TableCell>
                    <TableCell>{item.descripcion}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CatalogoSearchDialog;
